import { ProcessingJobType } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { Components } from "@/app/(components)/_components/Components";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import {
  useCanManageWorkspace,
  useCanRunPaidActions,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import {
  excludeCallFromAnalysis,
  recountCallInAnalysis,
} from "@/app/actions/call/analysis-scope/action";
import { useFetchRunningJobs } from "@/app/hooks/fetchs/user/useFetchRunningJobs";
import { useCreditGuard } from "@/app/hooks/useCreditGuard";
import { ModalStore } from "@/app/stores/ModalStore";
import { UserStore } from "@/app/stores/UserStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { CallFile, CallStatus } from "@/app/types/call/CallFile";
import { PageParams } from "@/app/types/common/DefaultProps";
import { CallAnalysisJobMetadata } from "@/app/types/job/ProcessingJob";
import { ProcessingPrice } from "@/app/vars/Credits";
import { QueryKey } from "@/app/vars/QueryKeys";

const RECOUNT_COST = ProcessingPrice[ProcessingJobType.CALL_INSIGHT_RECOUNT];

export type CallInsightScope = {
  available: boolean;
  excluded: boolean;
  recounting: boolean;
  canRunPaidActions: boolean;
  handleExclude: () => void;
  handleRecount: () => void;
};

export function useCallInsightScope(params: PageParams, callFile: CallFile): CallInsightScope {
  const T = useT();
  const queryClient = useQueryClient();
  const canManageWorkspace = useCanManageWorkspace();
  const canRunPaidActions = useCanRunPaidActions();
  const guardCredits = useCreditGuard(params);
  const decrementProcessingBalance = UserStore((state) => state.decrementProcessingBalance);
  const { data: runningJobs } = useFetchRunningJobs({ enabled: true });

  const excluded = callFile.savedCall?.excludedFromAnalysis ?? false;

  const recounting = (runningJobs ?? []).some((job) => {
    if (job.type !== ProcessingJobType.CALL_INSIGHT_RECOUNT) {
      return false;
    }
    const metadata = job.metadata as CallAnalysisJobMetadata | null;
    return metadata?.callId === callFile.id;
  });

  const invalidateScope = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.CALLS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_INSIGHTS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_PATTERNS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.CALL_ANALYSIS_OVERVIEW] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.CATEGORY_INSIGHTS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.INSIGHT_CALLS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_RECOMMENDATIONS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.HAS_PROJECT_INSIGHTS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_AVATAR] });
  };

  const handleExclude = () => {
    ModalStore.getState().openConfirmDialog(
      params,
      TKey.CALL_ANALYSIS_EXCLUDE_TITLE,
      TKey.CALL_ANALYSIS_EXCLUDE_DESC,
      TKey.CALL_ANALYSIS_EXCLUDE_CTA,
      async () => {
        await excludeCallFromAnalysis(callFile.id);
        invalidateScope();
      },
    );
  };

  const handleRecount = () => {
    if (!guardCredits(RECOUNT_COST)) {
      return;
    }
    ModalStore.getState().openModal(
      Components.CostConfirm,
      T(TKey.CALL_ANALYSIS_RECOUNT_TITLE) as string,
      {
        params,
        cost: RECOUNT_COST,
        ctaText: T(TKey.CALL_ANALYSIS_RECOUNT_CTA) as string,
        description: T(TKey.CALL_ANALYSIS_RECOUNT_DESC) as string,
        onConfirm: async () => {
          await recountCallInAnalysis(callFile.id);
          decrementProcessingBalance(RECOUNT_COST);
          queryClient.invalidateQueries({ queryKey: [QueryKey.RUNNING_JOBS] });
        },
      },
      ModalType.SMALL,
    );
  };

  return {
    available: callFile.status === CallStatus.ANALYZED && canManageWorkspace,
    excluded,
    recounting,
    canRunPaidActions,
    handleExclude,
    handleRecount,
  };
}
